import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import {
  CheckCircle,
  Pending,
  LocalShipping,
  Cancel,
  HourglassEmpty,
} from "@mui/icons-material";
import { Building2, Bell, Menu, X, User, LogOut } from "lucide-react";
import { orderAPI } from "../services/api";

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) setUser(JSON.parse(userData));
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await orderAPI.getOrders();
      if (response.data.success) {
        setOrders(response.data.data);
      }
    } catch (err) {
      setError("Failed to fetch orders");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return <CheckCircle color="success" />;
      case "confirmed":
        return <HourglassEmpty color="info" />;
      case "dispatched":
        return <LocalShipping color="primary" />;
      case "pending":
        return <Pending color="warning" />;
      case "cancelled":
        return <Cancel color="error" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "success";
      case "confirmed":
        return "info";
      case "dispatched":
        return "primary";
      case "pending":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ===== HEADER (Same as Dashboard) ===== */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center h-16">
          {/* Logo */}
          <div className="cursor-pointer" onClick={() => navigate("/")}>
            <img src="/logo.png" alt="Loha Mart" className="h-10 w-auto object-contain" />
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-6">
            <button onClick={() => navigate("/products")} className="nav-btn">
              Products
            </button>
            <button onClick={() => navigate("/orders")} className="nav-btn">
              Orders
            </button>
            <button onClick={() => navigate("/quotes")} className="nav-btn">
              Quotes
            </button>
            <button onClick={() => navigate("/finance")} className="nav-btn">
              Finance
            </button>
          </nav>

          {/* Right */}
          <div className="flex items-center space-x-4">
            <Bell className="w-5 h-5 text-gray-600" />

            <div className="hidden md:flex items-center space-x-3 pl-3 border-l">
              <div className="text-right">
                <p className="text-sm font-medium">
                  {user?.customer?.company_name || "User"}
                </p>
                <p className="text-xs text-gray-500">{user?.mobile}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <button onClick={handleLogout}>
                <LogOut className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Mobile menu */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden px-4 pb-4 space-y-2">
            <button
              onClick={() => navigate("/products")}
              className="mobile-btn"
            >
              Products
            </button>
            <button onClick={() => navigate("/orders")} className="mobile-btn">
              Orders
            </button>
            <button onClick={() => navigate("/quotes")} className="mobile-btn">
              Quotes
            </button>
            <button onClick={() => navigate("/finance")} className="mobile-btn">
              Finance
            </button>
            <button onClick={handleLogout} className="mobile-btn text-red-600">
              Logout
            </button>
          </div>
        )}
      </header>

      {/* ===== ORDERS CONTENT ===== */}
      <Container maxWidth="lg">
        <Box py={4}>
          <Typography variant="h4" gutterBottom>
            My Orders
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : orders.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography>No orders yet</Typography>
              <Button variant="contained" onClick={() => navigate("/quotes")}>
                View My Quotes
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {orders.map((order) => (
                <Grid item xs={12} key={order.id}>
                  <Card
                    onClick={() => navigate(`/orders/${order.id}`)}
                    sx={{ cursor: "pointer" }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" mb={2}>
                        <Box>
                          <Typography variant="h6">
                            {order.product?.name}
                          </Typography>
                          <Chip
                            icon={getStatusIcon(order.status)}
                            label={order.status.toUpperCase()}
                            color={getStatusColor(order.status)}
                            size="small"
                          />
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="caption">
                            {formatDate(order.created_at)}
                          </Typography>
                          <Typography variant="h6" color="primary">
                            ₹{order.total_amount}
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption">Order No</Typography>
                          <Typography fontWeight="bold">
                            {order.order_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption">Quantity</Typography>
                          <Typography fontWeight="bold">
                            {order.quantity}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption">Unit Price</Typography>
                          <Typography fontWeight="bold">
                            ₹{order.unit_price}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption">Supplier</Typography>
                          <Typography fontWeight="bold">
                            {order.supplier?.company_name || "Loha Mart"}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                        {order.invoice_url && (
                          <Chip
                            label="Invoice Available"
                            color="success"
                            size="small"
                          />
                        )}
                        {order.transaction_id && (
                          <Chip
                            label={order.payment_verified ? 'Payment Verified' : 'Pending Verification'}
                            color={order.payment_verified ? 'success' : 'warning'}
                            size="small"
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Container>
    </div>
  );
};

export default Orders;
